import React, { useEffect, useState } from "react";
import { CallClient } from "@azure/communication-calling";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Box,
  Alert,
} from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { DISPLAY } from "../../../common/constants/commonConstants";
import { UI_CONFIG } from "../../../common/constants/agentModule/incomingCallsConstants";
import { getPendingVoiceMail } from "../../../services/agentModule/voicemail/voiceMailService";
import { VOICEMAIL_ERRORS, VOICEMAIL_STRINGS } from "../../../common/constants/agentModule/voiceMailConstants";
import ProgressOverlay from "../../../common/components/ProgressOverlay";

const ALTERNATE_CALLER_ID = "+18335269779";

const sampleCustomers = [
  {
    id: "c1",
    name: "Fahd Mujtaba",
    phone: "2027791184",
    customerguid: "C96D0200-8322-42BA-8340-7AF93CF619F4",
  },
  {
    id: "c2",
    name: "Mary Johnson",
    phone: "8711112222",
    customerguid: "109060F8-A5A3-4C01-B944-DBD4CFBB78C3",
  },
  {
    id: "c3",
    name: "Robert Williams",
    phone: "9904445555",
    customerguid: "E83C4E02-1195-4A24-9A8D-35FFDE41214E",
  },
];

const VoiceMailCalls = ({ onCustomerGuid , refreshTrigger}) => {
  const [callAgent, setVoicemailAgent] = useState(null);
  const [currentVoicemail, setCurrentVoicemail] = useState(null);
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  const [selectedVoicemailCustomer, setSelectedVoicemailCustomer] =
    useState(null);
  const [selectedCustomeId, setSelectedCustomerId] = useState(null);
  const [voicemailCustomers, setVoicemailCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const phonePrefix = "+1";

  

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem("ACSIdentityToken");
      if (!storedToken) {
        console.error(VOICEMAIL_ERRORS.MISSING_ACS_TOKEN);
        return;
      }

      try {
        const client = new CallClient();
        const credential = new AzureCommunicationTokenCredential(storedToken);
        const agent = await client.createVoicemailAgent(credential);
        setVoicemailAgent(agent);
      } catch (err) {
        console.error(VOICEMAIL_ERRORS.INIT_AGENT_FAILED, err);
      }
    };

    init();
  }, []);

  useEffect(() => {
    fetchPendingVoicemails();
  }, [refreshTrigger]);

  const fetchPendingVoicemails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPendingVoiceMail('VmWithAgent', 'pending');
      setVoicemailCustomers(data || []);
    } catch (err) {
      console.error(VOICEMAIL_ERRORS.FETCH_PENDING_VOICEMAIL_ERROR, err);
      setError(VOICEMAIL_ERRORS.LOAD_VOICEMAILS_FAILED);
      setVoicemailCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDisplayNumber = (num) => {
    if (!num) return "";
  
    const raw = String(num).trim();
    const hadPlus = raw.startsWith("+");
    const digits = raw.replace(/\D/g, ""); // strip everything but digits
  
    // +1XXXXXXXXXX (E.164) → +1 (AAA) BBB-CCCC
    if (digits.length === 11 && digits[0] === "1") {
      const area = digits.slice(1, 4);
      const central = digits.slice(4, 7);
      const line = digits.slice(7);
      return `+1 (${area}) ${central}-${line}`;
    }
  
    // 10 digits → (AAA) BBB-CCCC
    if (digits.length === 10) {
      const area = digits.slice(0, 3);
      const central = digits.slice(3, 6);
      const line = digits.slice(6);
      return `(${area}) ${central}-${line}`;
    }
  
    // Fallbacks: partial input or non-US lengths
    if (digits.length > 6) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return hadPlus ? `+${digits}` : digits;
  };

  const startVoicemail = async (customer) => {
    if (!callAgent || currentVoicemail) return;

    const fullNumber = `${phonePrefix}${customer.phone}`;
    try {
      const call = callAgent.startVoicemail([{ phoneNumber: fullNumber }], {
        alternateVoicemailerId: { phoneNumber: ALTERNATE_CALLER_ID },
      });
      setCurrentVoicemail(call);
      setActiveCustomerId(customer.id);
    } catch (err) {
      console.error(VOICEMAIL_ERRORS.START_VOICEMAIL_ERROR, err);
    }
  };

  const hangUp = async () => {
    if (currentVoicemail) {
      try {
        await currentVoicemail.hangUp({ forEveryone: true });
      } catch (err) {
        console.error(VOICEMAIL_ERRORS.HANGUP_ERROR, err);
      }
      setCurrentVoicemail(null);
      setActiveCustomerId(null);
    }
  };

  const handleVoiceMailCustomerClick = (customerGuid, id, callconnectionid) => {
    setSelectedVoicemailCustomer(customerGuid);
    setSelectedCustomerId(id);
    onCustomerGuid(customerGuid, id, callconnectionid);
  };

  // const onConfirmCustomerSuccess = () => {
  //   fetchPendingVoicemails();
  // }

  return (
    <Box sx={{
      display: DISPLAY.FLEX,
      gap: UI_CONFIG.GAP,
      flexDirection: "column",
      mb: UI_CONFIG.MARGIN_BOTTOM,
    }}>
      <Box sx={{ display: DISPLAY.FLEX , gap: UI_CONFIG.GAP }}>
        <Typography mb={UI_CONFIG.MARGIN_BOTTOM}>{VOICEMAIL_STRINGS.VOICEMAIL_PANEL}</Typography>
      </Box>
       {isLoading && <ProgressOverlay />}
      {error && (
        <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
          {error}
        </Alert>
      )}

     
      
      <Stack spacing={2}>
        {voicemailCustomers.map((customer) => {
          const isActive = activeCustomerId === customer.jobid;
          return (
            <Card
              key={customer.jobid}
              sx={{
                mb: 2,
                cursor: "pointer",
                border:
                  selectedCustomeId === customer.jobid
                    ? "2px solid #1976d2"
                    : "1px solid #ccc",
                backgroundColor:
                selectedCustomeId === customer.jobid
                    ? "#e3f2fd"
                    : "#fff",
              }}
              onClick={() =>
                handleVoiceMailCustomerClick(customer.customerguid, customer.jobid, customer.callconnectionid)
              }
            >
              <CardContent>
                <Typography variant="inherit">
                  {customer.name}
                  <Chip
                    icon={<LocalPhoneIcon />}
                    label={formatDisplayNumber(customer.customeracsid)}
                    size="small"
                    sx={{ my: 1, ml: "5px" }}
                  />
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!!currentVoicemail && !isActive}
                    onClick={() => startVoicemail(customer)}
                  >
                    {VOICEMAIL_STRINGS.LISTEN}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!isActive}
                    onClick={hangUp}
                  >
                    {VOICEMAIL_STRINGS.STOP}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default VoiceMailCalls;
